/**
 * Tag Agent - Intelligent Tag Suggestions
 * 
 * This agent handles:
 * - Context-aware tag recommendations
 * - Hierarchical tag suggestions (1st, 2nd, 3rd order)
 * - Semantic tag matching
 * - Tag relevance scoring
 */

import { getFirstOrderTags } from '../data/tagHierarchy';

class TagAgent {
  constructor() {
    this.selectedHobbies = [];
    this.locationContext = null;
    this.userProfile = null;
  }

  /**
   * Set selected hobbies for tag suggestions
   */
  setHobbies(hobbies) {
    this.selectedHobbies = Array.isArray(hobbies) ? hobbies : [];
  }

  /**
   * Set location context
   */
  setLocationContext(location) {
    this.locationContext = location;
  }

  /**
   * Set user profile
   */
  setUserProfile(profile) {
    this.userProfile = profile;
  }

  /**
   * Get first-order tags based on selected hobbies
   */
  getFirstOrderTags() {
    if (this.selectedHobbies.length === 0) return [];

    // Get tags for the first selected hobby
    const primaryHobby = this.selectedHobbies[0];
    return getFirstOrderTags(primaryHobby);
  }

  /**
   * Suggest second-order tags based on first tag and hobby
   */
  suggestSecondOrderTags(firstTag, hobby) {
    // In production, this would use the tag hierarchy
    // For now, return common second-order tags
    const secondOrderMap = {
      'Protestant': ['Young Adult', 'Senior', 'Youth', 'Family'],
      'Catholic': ['Young Adult', 'Senior', 'Youth', 'Family'],
      'Basketball': ['Recreational', 'Competitive', 'Beginners', 'Advanced'],
      'Soccer': ['Recreational', 'Competitive', 'Beginners', 'Advanced'],
      'Book Club': ['Fiction', 'Non-fiction', 'Mystery', 'Sci-Fi']
    };

    return secondOrderMap[firstTag] || [];
  }

  /**
   * Suggest third-order tags based on first two tags
   */
  suggestThirdOrderTags(firstTag, secondTag) {
    // Common third-order tags
    return ['Weekly', 'Monthly', 'Bi-weekly', 'Daily', 'Casual', 'Formal'];
  }

  /**
   * Get intelligent tag suggestions
   */
  getIntelligentTags(options = {}) {
    const {
      maxFirstOrder = 10,
      maxSecondOrder = 5,
      maxThirdOrder = 5
    } = options;

    const suggestions = {
      firstOrder: [],
      secondOrder: [],
      thirdOrder: []
    };

    // Get first-order tags from hobbies
    if (this.selectedHobbies.length > 0) {
      suggestions.firstOrder = this.getFirstOrderTags().slice(0, maxFirstOrder);
    }

    // If a first tag is selected, suggest second-order tags
    if (options.firstTag && this.selectedHobbies.length > 0) {
      const secondOrder = this.suggestSecondOrderTags(
        options.firstTag,
        this.selectedHobbies[0]
      );
      suggestions.secondOrder = secondOrder.slice(0, maxSecondOrder);
    }

    // If first two tags are selected, suggest third-order tags
    if (options.firstTag && options.secondTag) {
      const thirdOrder = this.suggestThirdOrderTags(
        options.firstTag,
        options.secondTag
      );
      suggestions.thirdOrder = thirdOrder.slice(0, maxThirdOrder);
    }

    return suggestions;
  }

  /**
   * Rank tags by relevance
   */
  rankTags(tags, context = {}) {
    return tags.map(tag => ({
      tag,
      score: this.calculateTagRelevance(tag, context)
    })).sort((a, b) => b.score - a.score);
  }

  /**
   * Calculate relevance score for a tag
   */
  calculateTagRelevance(tag, context) {
    let score = 0;
    const tagLower = tag.toLowerCase();

    // Check if tag matches hobby
    if (this.selectedHobbies.some(hobby => 
      hobby.toLowerCase().includes(tagLower) ||
      tagLower.includes(hobby.toLowerCase())
    )) {
      score += 10;
    }

    // Check if tag matches user profile
    if (this.userProfile?.tags?.some(t => t.toLowerCase() === tagLower)) {
      score += 8;
    }

    // Check location context
    if (this.locationContext) {
      // Tags related to location (e.g., "Seattle", "University District")
      if (tagLower.includes(this.locationContext.city?.toLowerCase() || '')) {
        score += 5;
      }
    }

    return score;
  }

  /**
   * Validate tag completeness (should have 3 tags for group creation)
   */
  validateTags(tags, required = false) {
    if (!required) {
      return { valid: true, tags };
    }

    if (!tags || tags.length !== 3) {
      return {
        valid: false,
        error: 'Exactly 3 tags are required (1st order, 2nd order, and 3rd order tags)',
        tags
      };
    }

    return { valid: true, tags };
  }

  /**
   * Get semantic tag suggestions (related tags)
   */
  getSemanticSuggestions(tag) {
    // Semantic relationships
    const semanticMap = {
      'Protestant': ['Christian', 'Evangelical', 'Baptist'],
      'Catholic': ['Roman Catholic', 'Christian'],
      'Basketball': ['Sports', 'Basketball', 'Team Sports'],
      'Book Club': ['Reading', 'Literature', 'Books']
    };

    return semanticMap[tag] || [];
  }
}

export default TagAgent;

